/**
 * ExtractionViewerPage
 *
 * A page that displays the source document alongside extracted sales order data,
 * with bounding box highlighting showing where each field was extracted from.
 *
 * Accessed via: /sales-order-drafts/:draftId/extraction-viewer
 */

import { useMemo, useCallback, useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileWarning, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertContainer as Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { DocumentComparisonLayout } from '@/components/document-comparison/DocumentComparisonLayout';
import { DocumentComparisonProvider } from '@/components/document-comparison/DocumentComparisonProvider';
import type { ColumnConfig } from '@/components/document-comparison/columns/types';
import type {
  Citation,
  ExtendAIExtractionOutput,
} from '@/components/document-comparison/types';
import { useExtractionViewerData } from './hooks/useExtractionViewerData';
import { SalesOrderDataPanel } from './components/SalesOrderDataPanel';

/**
 * Build citation data from extraction metadata.
 * Returns both the citations array and the citation-to-field map,
 * using the SAME Citation object instances so Map lookups work.
 */
function buildCitationData(extraction: ExtendAIExtractionOutput | null): {
  citations: Citation[];
  citationFieldMap: Map<Citation, string>;
} {
  const citations: Citation[] = [];
  const citationFieldMap = new Map<Citation, string>();

  if (!extraction?.metadata) {
    return { citations, citationFieldMap };
  }

  for (const [fieldPath, fieldMeta] of Object.entries(extraction.metadata)) {
    if (fieldMeta.citations) {
      for (const citation of fieldMeta.citations) {
        if (
          citation.page !== undefined &&
          citation.polygon &&
          citation.polygon.length > 0
        ) {
          // Create a single Citation object used in both structures
          const normalizedCitation: Citation = {
            page: citation.page,
            referenceText: citation.referenceText,
            polygon: citation.polygon,
            pageWidth: citation.pageWidth,
            pageHeight: citation.pageHeight,
          };
          citations.push(normalizedCitation);
          citationFieldMap.set(normalizedCitation, fieldPath);
        }
      }
    }
  }

  return { citations, citationFieldMap };
}

/**
 * Determine document type from MIME type.
 */
function getDocumentType(mimeType: string): 'pdf' | 'image' {
  if (mimeType === 'application/pdf') {
    return 'pdf';
  }
  if (mimeType.startsWith('image/')) {
    return 'image';
  }
  // Default to PDF for unknown types
  return 'pdf';
}

export function ExtractionViewerPage() {
  const params = useParams({ strict: false }) as { draftId: string };
  const navigate = useNavigate();
  const draftId = params.draftId;

  // Track current page for navigation
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch data
  const {
    document,
    extraction,
    purchaseOrderIndex,
    salesOrderDraft,
    isLoading,
    extractionError,
    draftError,
  } = useExtractionViewerData({
    salesOrderDraftId: draftId,
  });

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate({ to: '/sales-orders', search: { salesOrderId: draftId } });
  }, [navigate, draftId]);

  // Handle page navigation from field hover
  const handleNavigateToPage = useCallback(
    (_columnId: string, page: number) => {
      setCurrentPage(page);
    },
    [],
  );

  // Build extraction output in the format expected by the provider
  const extractionOutput = useMemo((): ExtendAIExtractionOutput | undefined => {
    if (!extraction) return undefined;
    return {
      value: extraction.value,
      metadata: extraction.metadata,
    };
  }, [extraction]);

  // Build column configurations
  const columns = useMemo((): ColumnConfig[] => {
    if (!document) return [];

    const { citations: allCitations, citationFieldMap } = buildCitationData(
      extractionOutput ?? null,
    );
    const docType = getDocumentType(document.mimeType);

    return [
      // Document viewer column
      {
        id: 'document',
        type: 'document' as const,
        title: document.originalFileName || 'Source Document',
        documentUrl: document.url,
        documentType: docType,
        mimeType: document.mimeType,
        citations: allCitations,
        citationFieldMap,
        defaultWidthPercent: 55,
        minWidthPx: 400,
      },
      // Data panel column
      {
        id: 'data',
        type: 'data' as const,
        title: 'Extracted Data',
        defaultWidthPercent: 45,
        minWidthPx: 350,
        render: (context) => {
          if (!salesOrderDraft) {
            return (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No draft data available
              </div>
            );
          }
          return (
            <SalesOrderDataPanel
              draft={salesOrderDraft}
              context={context}
              purchaseOrderIndex={purchaseOrderIndex}
            />
          );
        },
      },
    ];
  }, [document, extractionOutput, salesOrderDraft, purchaseOrderIndex]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen">
        <div className="flex-shrink-0 flex items-center py-3 px-3 bg-muted/20">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">
              Loading extraction viewer...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (extractionError || draftError) {
    return (
      <div className="flex flex-col h-screen">
        <div className="flex-shrink-0 flex items-center py-3 px-3 bg-muted/20">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <Alert variant="destructive" className="max-w-md">
            <FileWarning className="h-4 w-4" />
            <AlertTitle>Failed to load data</AlertTitle>
            <AlertDescription>
              {extractionError?.message ||
                draftError?.message ||
                'An error occurred while loading the extraction viewer.'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // No document state
  if (!document) {
    return (
      <div className="flex flex-col h-screen">
        <div className="flex-shrink-0 flex items-center py-3 px-3 bg-muted/20">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <Alert className="max-w-md">
            <FileWarning className="h-4 w-4" />
            <AlertTitle>No source document</AlertTitle>
            <AlertDescription>
              This sales order draft does not have a source document to display.
              The document may have been created manually or the original
              attachment is not available.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // No extraction metadata state (document exists but no AI extraction)
  if (!extraction) {
    return (
      <div className="flex flex-col h-screen">
        <div className="flex-shrink-0 flex items-center py-3 px-3 bg-muted/20">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <Alert className="max-w-md">
            <FileWarning className="h-4 w-4" />
            <AlertTitle>No extraction metadata</AlertTitle>
            <AlertDescription>
              The source document exists, but extraction metadata is not
              available. This may happen if the document was processed before
              citation metadata was captured.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Main viewer
  return (
    <div className="flex flex-col h-screen">
      {/* Header with back button */}
      <div className="flex-shrink-0 flex items-center py-3 px-3 bg-muted/20">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Full screen layout */}
      <div className="flex-1 min-h-0">
        <DocumentComparisonProvider
          extractionOutput={extractionOutput}
          onNavigateToPage={handleNavigateToPage}
        >
          <DocumentComparisonLayout columns={columns} className="h-full" />
        </DocumentComparisonProvider>
      </div>
    </div>
  );
}

export default ExtractionViewerPage;
